import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { companiesAPI, currenciesAPI } from '../../services/api';
import { CreateCompanyDto, UpdateCompanyDto, CompanyStatus, Currency } from '../../types';
import { toastSuccess } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

const CompanyForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [formData, setFormData] = useState<CreateCompanyDto>({
    name: '',
    description: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    website: '',
    status: CompanyStatus.ACTIVE,
    logo: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  useEffect(() => {
    if (isEditing) {
      loadCompany();
    }
    loadCurrencies();
  }, [id]);

  const loadCompany = async () => {
    try {
      setLoading(true);
      const company = await companiesAPI.getById(id!);
      setFormData({
        name: company.name,
        description: company.description || '',
        email: company.email,
        phone: company.phone || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
        postalCode: company.postalCode || '',
        website: company.website || '',
        status: company.status,
        logo: company.logo || '',
        defaultCurrencyId: company.defaultCurrencyId,
      });
    } catch (err) {
      setError(t('companyForm.errorLoad'));
      console.error('Error loading company:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrencies = async () => {
    try {
      const list = await currenciesAPI.getAll();
      setCurrencies(list);
    } catch (e) {
      console.error('Failed to load currencies', e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      if (isEditing) {
        await companiesAPI.update(id!, formData as UpdateCompanyDto);
      } else {
        await companiesAPI.create(formData);
      }
      toastSuccess(isEditing ? t('companyForm.successUpdate') : t('companyForm.successCreate'));
      navigate('/companies');
    } catch (err: any) {
      console.error('Error saving company:', err);
      
      // Handle backend validation errors
      if (err.response?.data?.message) {
        const errorData = err.response.data;
        
        // If it's an array of validation errors
        if (Array.isArray(errorData.message)) {
          const newFieldErrors: Record<string, string> = {};
          errorData.message.forEach((errorMsg: string) => {
            // Parse validation error messages like "email must be an email"
            const fieldMatch = errorMsg.match(/^(\w+)\s+(.+)$/);
            if (fieldMatch) {
              const [, field, message] = fieldMatch;
              newFieldErrors[field] = message;
            } else {
              // If we can't parse the field, add to general error
              setError(errorMsg);
            }
          });
          setFieldErrors(newFieldErrors);
        } else {
          // Single error message
          setError(errorData.message);
        }
      } else {
        setError(t('companyForm.errorSave'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const getFieldError = (fieldName: string) => {
    return fieldErrors[fieldName] || '';
  };

  const getFieldClassName = (fieldName: string) => {
    const baseClass = "mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
    return fieldErrors[fieldName] 
      ? `${baseClass} border-red-300 focus:ring-red-500 focus:border-red-500`
      : baseClass;
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? t('companyForm.titleEdit') : t('companyForm.titleCreate')}
        </h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              {t('companyForm.name')} *
            </label>
            <input
              type="text"
              name="name"
              id="name"
              required
              value={formData.name}
              onChange={handleChange}
              className={getFieldClassName('name')}
            />
            {getFieldError('name') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('name')}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              {t('companyForm.email')} *
            </label>
            <input
              type="email"
              name="email"
              id="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={getFieldClassName('email')}
            />
            {getFieldError('email') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('email')}</p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              {t('companyForm.phone')}
            </label>
            <input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              className={getFieldClassName('phone')}
            />
            {getFieldError('phone') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('phone')}</p>
            )}
          </div>

          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700">
              {t('companyForm.website')}
            </label>
            <input
              type="url"
              name="website"
              id="website"
              value={formData.website}
              onChange={handleChange}
              className={getFieldClassName('website')}
            />
            {getFieldError('website') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('website')}</p>
            )}
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              {t('companyForm.status')}
            </label>
            <select
              name="status"
              id="status"
              value={formData.status}
              onChange={handleChange}
              className={getFieldClassName('status')}
            >
              <option value={CompanyStatus.ACTIVE}>{t('companies.status.ACTIVE')}</option>
              <option value={CompanyStatus.INACTIVE}>{t('companies.status.INACTIVE')}</option>
              <option value={CompanyStatus.SUSPENDED}>{t('companies.status.SUSPENDED')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="logo" className="block text-sm font-medium text-gray-700">
              {t('companyForm.logo')}
            </label>
            <input
              type="url"
              name="logo"
              id="logo"
              value={formData.logo}
              onChange={handleChange}
              className={getFieldClassName('logo')}
            />
            {getFieldError('logo') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('logo')}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            {t('companyForm.description')}
          </label>
          <textarea
            name="description"
            id="description"
            rows={3}
            value={formData.description}
            onChange={handleChange}
            className={getFieldClassName('description')}
          />
          {getFieldError('description') && (
            <p className="mt-1 text-sm text-red-600">{getFieldError('description')}</p>
          )}
        </div>

        <div>
          <label htmlFor="defaultCurrencyId" className="block text-sm font-medium text-gray-700">
            {t('companyForm.defaultCurrency')}
          </label>
          <select
            name="defaultCurrencyId"
            id="defaultCurrencyId"
            value={(formData as any).defaultCurrencyId || ''}
            onChange={handleChange}
            className={getFieldClassName('defaultCurrencyId')}
          >
            <option value="">{t('companyForm.selectCurrency')}</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} - {c.name}
              </option>
            ))}
          </select>
          {getFieldError('defaultCurrencyId') && (
            <p className="mt-1 text-sm text-red-600">{getFieldError('defaultCurrencyId')}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
              {t('companyForm.address')}
            </label>
            <input
              type="text"
              name="address"
              id="address"
              value={formData.address}
              onChange={handleChange}
              className={getFieldClassName('address')}
            />
            {getFieldError('address') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('address')}</p>
            )}
          </div>

          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              {t('companyForm.city')}
            </label>
            <input
              type="text"
              name="city"
              id="city"
              value={formData.city}
              onChange={handleChange}
              className={getFieldClassName('city')}
            />
            {getFieldError('city') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('city')}</p>
            )}
          </div>

          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700">
              {t('companyForm.state')}
            </label>
            <input
              type="text"
              name="state"
              id="state"
              value={formData.state}
              onChange={handleChange}
              className={getFieldClassName('state')}
            />
            {getFieldError('state') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('state')}</p>
            )}
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
              {t('companyForm.country')}
            </label>
            <input
              type="text"
              name="country"
              id="country"
              value={formData.country}
              onChange={handleChange}
              className={getFieldClassName('country')}
            />
            {getFieldError('country') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('country')}</p>
            )}
          </div>

          <div>
            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
              {t('companyForm.postalCode')}
            </label>
            <input
              type="text"
              name="postalCode"
              id="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              className={getFieldClassName('postalCode')}
            />
            {getFieldError('postalCode') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('postalCode')}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/companies')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t('companyForm.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? t('companyForm.saving') : isEditing ? t('companyForm.update') : t('companyForm.create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;
